/**
 * Vercel Blob File Storage
 *
 * This file provides functions for managing file uploads using Vercel Blob storage.
 * It mirrors the interface of the existing Supabase storage functions for easy integration.
 */
import { toast } from 'sonner'
import { put, del } from '@vercel/blob'
import { safeConsole } from '@/lib/utils/safeConsole'

/**
 * Upload a file to Vercel Blob storage
 *
 * @param file The file to upload
 * @param payload File metadata including user_id and file_id
 * @returns The blob path for database storage
 */
export const uploadFileToBlob = async (
  file: File | Blob,
  payload: {
    name: string
    user_id: string
    file_id: string
  }
) => {
  const SIZE_LIMIT = 500 * 1024 * 1024 // 500MB

  if (file.size > SIZE_LIMIT) {
    throw new Error(`File must be less than ${SIZE_LIMIT / 1000000}MB`)
  }

  // Create a path similar to the Supabase storage pattern for consistency
  const filePath = `${payload.user_id}/${Buffer.from(payload.file_id).toString('base64')}`

  try {
    // Upload to Vercel Blob
    const { url, pathname } = await put(filePath, file, {
      access: 'public',
      addRandomSuffix: false, // Use our exact filename
      cacheControlMaxAge: 31536000 // 1 year cache
    })

    // Store both the pathname and URL in a JSON string for flexibility
    const blobData = JSON.stringify({ pathname, url })

    return {
      filePath: blobData, // Store the JSON data as the filePath
      url // Return URL for immediate use
    }
  } catch (error) {
    safeConsole.error(`Error uploading file to Vercel Blob with path: ${filePath}`, error)
    throw new Error('Error uploading file to Vercel Blob')
  }
}

/**
 * Delete a file from Vercel Blob storage
 *
 * @param blobDataString JSON string containing pathname and URL
 */
export const deleteFileFromBlobStorage = async (blobDataString: string) => {
  try {
    const blobData = JSON.parse(blobDataString)

    if (!blobData.pathname) {
      throw new Error('Invalid blob data format')
    }

    await del(blobData.pathname)
  } catch (error) {
    safeConsole.error('Failed to remove file from Vercel Blob!', error)
    toast.error('Failed to remove file!')
  }
}

/**
 * Get a signed URL for a file from Vercel Blob
 *
 * @param blobDataString JSON string containing pathname and URL
 * @returns The URL for the file
 */
export const getFileFromBlobStorage = async (blobDataString: string) => {
  try {
    // Handle both new JSON format and legacy direct URL format
    if (blobDataString.startsWith('{')) {
      // New JSON format: {"pathname": "...", "url": "..."}
      const blobData = JSON.parse(blobDataString)

      // If the URL is already in the blob data, return it directly
      if (blobData.url) {
        return blobData.url
      }

      // If we don't have a URL, we can't access the file
      // Note: Vercel Blob URLs should be stored when uploading
      if (blobData.pathname) {
        throw new Error('URL not available for this blob')
      }

      throw new Error('Invalid blob data format')
    } else {
      // Legacy format: direct URL string
      // This handles existing files that were stored as direct URLs
      return blobDataString
    }
  } catch (error) {
    safeConsole.error('Error getting file from Vercel Blob', error)
    safeConsole.error('Blob data string:', blobDataString)
    throw new Error('Error accessing file')
  }
}
